import React, { useCallback } from "react";
import { FieldArray, Form, Formik } from "formik";
import { Button, IconButton, InputLabel, TextField, Box, Grid } from "@mui/material";
import { styled } from '@mui/material/styles';
import ClientFilter, { defaultFilter } from "../../core/api/clients/ClientFilter";
import ClientStateSelectInput from "../classifier/ClientStateSelectInput";
import { DatePicker } from "../common/DatePicker";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { format, parseISO, isValid } from "date-fns";
import { useNavigate } from "react-router-dom";
import qs from "qs";
import { translations } from "../../translations/common";
import UserSelectInput from "../classifier/UserSelectInput";

const RootForm = styled(Form)({
  width: '100%',
});

const MarginContainer = styled(Grid)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const DatePickerContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const InputsContainer = styled(Box)({
  width: '100%',
  '& .MuiInputBase-root': { width: '100%' },
  '& .MuiOutlinedInput-root': { width: '100%' },
  '& .MuiFormControl-root': { width: '100%' }
});

const ClientIdList = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  margin: theme.spacing(1)
}));

interface ClientFilterFormProps {
  filter: ClientFilter;
}

interface ClientIdFieldProps {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  onRemove: () => void;
}

const ClientIdField: React.FC<ClientIdFieldProps> = ({
  id,
  value,
  onChange,
  onRemove,
}) => (
  <StyledTextField
    id={id}
    type="text"
    name={id}
    onChange={onChange}
    value={value}
    fullWidth
    sx={{ margin: 0 }}
    InputProps={{
      endAdornment: (
        <IconButton
          color="secondary"
          aria-label={translations.clientFilterForm.actions.removeId}
          component="span"
          onClick={onRemove}
          size="small">
          <RemoveIcon fontSize="small" />
        </IconButton>
      ),
    }}
  />
);

const DATE_FORMAT = "yyyy-MM-dd";

// Format function to ensure consistent date format
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '';

  try {
    const date = parseISO(dateStr);
    if (isValid(date)) {
      return format(date, DATE_FORMAT);
    }
  } catch (error) {
    console.error('Invalid date format:', error);
  }

  return dateStr;
};

/**
 * Form component for filtering clients based on various criteria
 */
export const ClientFilterForm: React.FC<ClientFilterFormProps> = ({ filter }) => {
  const navigate = useNavigate();

  const handleSubmit = useCallback((values: ClientFilter) => {
    navigate({
      search: qs.stringify(values, {
        serializeDate: (date: Date) => format(date, DATE_FORMAT),
      }),
    }, { replace: true });
  }, [navigate]);

  const handleReset = useCallback(() => {
    // Redirect to a URL with empty filters
    navigate({
      search: '',
    }, { replace: true });
    }, [navigate]);

  // Ensure date fields are properly formatted in filter
  const formattedFilter = {
    ...filter,
    validFrom: formatDate(filter.validFrom),
    validTo: formatDate(filter.validTo)
  };

  return (
    <Formik
      initialValues={formattedFilter}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, errors, resetForm }) => (
        <RootForm>
          <InputsContainer>
            <MarginContainer
              container
              direction="column"
              justifyContent="center"
              alignItems="stretch"
              spacing={1}
            >
              <Grid size={12}>
                <StyledTextField
                  id="filter"
                  type="text"
                  name="filter"
                  label={translations.clientFilterForm.filter}
                  onChange={handleChange}
                  value={values.filter}
                  fullWidth
                />
              </Grid>

              <Grid size={12}>
                <ClientStateSelectInput
                  sx={{ margin: (theme) => theme.spacing(1), width: '100%' }}
                  name="state"
                  fullWidth
                  id="user-type-select"
                  value={values.state}
                  onChange={handleChange}
                  label="Kliento statusas"
                />
              </Grid>

              {!values.myClients && (
                <Grid size={12}>
                  <UserSelectInput
                    sx={{ margin: (theme) => theme.spacing(1), width: '100%' }}
                    name="responsibleUser"
                    fullWidth
                    id="responsible-user-select"
                    value={values.responsibleUser}
                    onChange={handleChange}
                    label="Atsakingas asmuo"
                    variant="outlined"
                  />
                </Grid>
              )}

              <Grid size={12}>
                <DatePickerContainer>
                  <DatePicker
                    id="validFrom"
                    name="validFrom"
                    label={translations.clientFilterForm.validFrom}
                    value={values.validFrom}
                    onChange={handleChange}
                    fullWidth
                  />
                </DatePickerContainer>
              </Grid>

              <Grid size={12}>
                <DatePickerContainer>
                  <DatePicker
                    id="validTo"
                    name="validTo"
                    label={translations.clientFilterForm.validTo}
                    value={values.validTo}
                    onChange={handleChange}
                    fullWidth
                  />
                </DatePickerContainer>
              </Grid>

              <Grid size={12}>
                <StyledTextField
                  id="idFrom"
                  type="number"
                  name="idFrom"
                  label={translations.clientFilterForm.idFrom}
                  onChange={handleChange}
                  value={values.idFrom}
                  fullWidth
                />
              </Grid>

              <Grid size={12}>
                <StyledTextField
                  id="idTo"
                  type="number"
                  name="idTo"
                  label={translations.clientFilterForm.idTo}
                  onChange={handleChange}
                  value={values.idTo}
                  fullWidth
                />
              </Grid>

              <Grid size={12}>
                <ClientIdList>
                  <InputLabel sx={{ pl: 1 }}>{translations.clientFilterForm.clientIdList}</InputLabel>
                  <FieldArray
                    name="ids"
                    render={arrayHelpers => (
                      <>
                        {values.ids && values.ids.length > 0 ? (
                          values.ids.map((id: string, index: number) => (
                            <ClientIdField
                              key={index}
                              id={`ids.${index}`}
                              value={id}
                              onChange={handleChange}
                              onRemove={() => arrayHelpers.remove(index)}
                            />
                          ))
                        ) : null}
                        <StyledButton
                          startIcon={<AddIcon />}
                          onClick={() => arrayHelpers.push('')}
                          variant="outlined"
                          color="primary"
                          sx={{ margin: 0 }}
                        >
                          {translations.clientFilterForm.actions.addId}
                        </StyledButton>
                      </>
                    )}
                  />
                </ClientIdList>
              </Grid>

              <Grid size={12}>
                <StyledButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  {translations.common.filter}
                </StyledButton>
                <StyledButton
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    resetForm({ values: defaultFilter });
                    handleReset();
                  }}
                  fullWidth
                >
                  {translations.search.clearLabel}
                </StyledButton>
              </Grid>
            </MarginContainer>
          </InputsContainer>
        </RootForm>
      )}
    </Formik>
  );
};

export default ClientFilterForm;
